import { createContext as e, useCallback as t, useContext as n, useEffect as r, useMemo as i, useRef as a, useState as o } from "react";
import { jsx as s, jsxs as c } from "react/jsx-runtime";
import { useMutation as l, useQuery as u, useQueryClient as ee } from "@tanstack/react-query";
import d, { isAxiosError as f } from "axios";
import { APP_CHAT_RICH_PART_TYPES as p, CHAT_UPLOAD_MAX_BYTES as m, MAX_CHAT_ATTACHMENTS_PER_MESSAGE as h, REGION_TILE_LAYERS as te, chatWithAppContract as ne, getProcessChainTreeContract as g, getProcessLayoutContract as _, getRegionTilesContract as v, isChatUploadMediaType as re, isReservedChatMessageId as ie, listExternalContactsContract as y, listOrgFunctionsContract as b, listOrgMembersContract as x, listSystemsContract as S, loadAppChatHistoryContract as ae, presignAppChatUploadContract as C, settledToolPartFields as w, startAppChatContract as oe, transcribeAudioContract as se } from "@bliks-io/contracts";
import { CHAT_UPLOAD_ACCEPT as T, Page as E, PageContent as D, PageHeader as O, Tabs as k, TabsList as A, TabsTrigger as j, audioBlobFilename as M, buildAssigneeLookup as N, uploadToPresignedPost as P, useChatAttachmentDrafts as F } from "@bliks-io/ui";
import { useChat as ce } from "@ai-sdk/react";
import { DefaultChatTransport as le, getToolName as I, isToolUIPart as L } from "ai";
//#region src/react/context.tsx
var R = e(null);
function ue({ value: e, children: t }) {
	return /* @__PURE__ */ s(R.Provider, {
		value: e,
		children: t
	});
}
function z() {
	let e = n(R);
	if (!e) throw Error("useAppContext must be used within an AppContextProvider");
	return e;
}
//#endregion
//#region src/react/hooks/use-ai-feature.ts
function de(e) {
	let { ai: t } = z();
	return t?.enabled === !0 && t.features[e] === !0;
}
//#endregion
//#region src/react/hooks/use-api-client.ts
function fe(e) {
	if (!f(e)) return null;
	let t = e.response?.data;
	if (typeof t != "object" || !t || !("message" in t)) return null;
	let { message: n } = t;
	return typeof n == "string" && n.trim() ? n : Array.isArray(n) && n.every((e) => typeof e == "string") ? n.join(" ") : null;
}
function B() {
	let { apiBaseUrl: e, accessToken: t } = z();
	return i(() => {
		let n = d.create({
			baseURL: e,
			headers: { Authorization: `Bearer ${t}` }
		});
		return n.interceptors.response.use(void 0, (e) => {
			let t = fe(e);
			return t && e instanceof Error && (e.message = t), Promise.reject(e);
		}), n;
	}, [e, t]);
}
//#endregion
//#region src/react/hooks/use-data.ts
function pe(e) {
	let t = B();
	return u({
		...e,
		queryKey: ["processes", "list"],
		queryFn: async ({ signal: e }) => {
			let { data: n } = await t.get("/api/v2/process", { signal: e });
			return n.processes ?? [];
		}
	});
}
function me(e, t) {
	return {
		queryKey: [
			"processes",
			"detail",
			t
		],
		queryFn: async ({ signal: n }) => {
			let { data: r } = await e.get(`/api/v2/process/${t}`, { signal: n });
			return r;
		}
	};
}
function he(e, t, n) {
	return u({
		...n,
		...e,
		enabled: (n?.enabled ?? !0) && !!t
	});
}
function ge(e, t) {
	return he(me(B(), e), e, t);
}
function _e() {
	let e = B();
	return u({
		queryKey: ["process-chains", "tree"],
		queryFn: async ({ signal: t }) => {
			let { data: n } = await e.get(g.path, { signal: t });
			return n;
		}
	});
}
//#endregion
//#region src/react/hooks/use-process-layout.ts
function ve(e, t = "published", n) {
	let r = B();
	return u({
		...n,
		queryKey: [
			"process",
			"layout",
			e,
			t
		],
		queryFn: async ({ signal: n }) => {
			let { data: i } = await r.get(_.path.replace(":processId", e), {
				params: { scope: t },
				signal: n
			});
			return {
				savedPositions: [...i.stepPositions.map((e) => ({
					stepId: String(e.stableStepId),
					x: e.x,
					y: e.y
				})), ...i.generatedNodePositions.map((e) => ({
					stepId: e.nodeId,
					x: e.x,
					y: e.y
				}))],
				swimlaneOrder: i.swimlaneOrder,
				laneColors: i.laneColors
			};
		},
		enabled: (n?.enabled ?? !0) && !!e
	});
}
//#endregion
//#region src/react/hooks/use-assignee-lookup.ts
function V() {
	let e = B();
	return u({
		queryKey: ["org-lookup", "org-member"],
		queryFn: async ({ signal: t }) => {
			let { data: n } = await e.get(`${x.path}?includeDeleted=true`, { signal: t });
			return n;
		}
	});
}
function H() {
	let e = B();
	return u({
		queryKey: ["org-lookup", "org-function"],
		queryFn: async ({ signal: t }) => {
			let { data: n } = await e.get(`${b.path}?includeDeleted=true`, { signal: t });
			return n.data;
		}
	});
}
function U() {
	let e = B();
	return u({
		queryKey: ["org-lookup", "system"],
		queryFn: async ({ signal: t }) => {
			let { data: n } = await e.get(`${S.path}?includeDeleted=true`, { signal: t });
			return n.data;
		}
	});
}
function W() {
	let e = B();
	return u({
		queryKey: ["org-lookup", "external-contact"],
		queryFn: async ({ signal: t }) => {
			let { data: n } = await e.get(`${y.path}?includeDeleted=true`, { signal: t });
			return n.data;
		}
	});
}
var G = Object.freeze([]);
function ye() {
	let e = V(), t = H(), n = U(), r = W();
	return i(() => N({
		users: e.data ?? G,
		orgFunctions: t.data ?? G,
		systems: n.data ?? G,
		externalContacts: r.data ?? G
	}), [
		e.data,
		t.data,
		n.data,
		r.data
	]);
}
//#endregion
//#region src/react/hooks/use-app-function.ts
function be(e, t) {
	let n = B(), { app: r } = z();
	return l({
		...t,
		mutationFn: async (t) => {
			let { data: i } = await n.post(`/api/v2/apps/${r.id}/fn/${e}`, t);
			return i;
		}
	});
}
function K(e, t) {
	return [
		"apps",
		e,
		"fn",
		t
	];
}
function xe(e, t, n) {
	let r = B(), { app: i } = z();
	return u({
		...n,
		queryKey: [...K(i.id, e), t ?? {}],
		queryFn: async ({ signal: n }) => {
			let { data: a } = await r.post(`/api/v2/apps/${i.id}/fn/${e}`, t ?? {}, { signal: n });
			return a.result;
		}
	});
}
function Se(e) {
	let t = B(), { app: n } = z();
	return l({ mutationFn: async (r) => {
		let { data: i } = await t.post(`/api/v2/apps/${n.id}/fn/${e}/async`, r);
		return i;
	} });
}
//#endregion
//#region src/react/hooks/use-region-tiles.ts
function Ce() {
	let { apiBaseUrl: e, accessToken: t } = z();
	return i(() => ({
		url: `${e}${v.path}`,
		headers: { Authorization: `Bearer ${t}` },
		layers: te
	}), [e, t]);
}
//#endregion
//#region src/react/hooks/use-transcribe-audio.ts
function we() {
	let e = B();
	return l({ mutationFn: async (t) => {
		let n = new FormData();
		n.append("audio", t, M(t));
		let { data: r } = await e.post(se.path, n, { timeout: 6e4 });
		return r.text;
	} });
}
//#endregion
//#region src/react/hooks/use-app-storage.ts
function q(e, t) {
	return [
		"apps",
		e,
		"storage",
		t
	];
}
function J(e, t, n, r) {
	return {
		queryKey: [...q(t, n), r ?? {}],
		queryFn: async ({ signal: i }) => {
			let { data: a } = await e.get(`/api/v2/apps/${t}/storage/${n}`, {
				params: r,
				signal: i
			});
			return a.data;
		}
	};
}
function Te(e, t, n) {
	let r = B(), { app: i } = z();
	return u({
		...n,
		...J(r, i.id, e, t)
	});
}
function Y(e, t, n, r) {
	return {
		queryKey: [...q(t, n), r],
		queryFn: async ({ signal: i }) => {
			let { data: a } = await e.get(`/api/v2/apps/${t}/storage/${n}/${r}`, { signal: i });
			return a.data;
		}
	};
}
function Ee(e, t, n) {
	let r = B(), { app: i } = z();
	return u({
		...n,
		...Y(r, i.id, e, t),
		enabled: (n?.enabled ?? !0) && !!t
	});
}
//#endregion
//#region src/react/lib/app-chat-path.ts
function X(e, t) {
	let n = e.replace(":appId", encodeURIComponent(t.appId)).replace(":kind", encodeURIComponent(t.kind));
	return t.sessionId !== void 0 && (n = n.replace(":sessionId", encodeURIComponent(t.sessionId))), n;
}
//#endregion
//#region src/react/lib/app-chat-messages.ts
function De(e) {
	return e.type === "text" ? {
		type: "text",
		text: e.text
	} : e.type === "file" ? {
		type: "file",
		mediaType: e.mediaType,
		filename: e.filename,
		url: ""
	} : e.type === "tool-app" ? {
		type: `tool-${e.toolName}`,
		toolCallId: e.toolCallId,
		input: e.input,
		...w(e)
	} : e;
}
function Oe(e) {
	return e.parts && e.parts.length > 0 ? e.parts.map(De) : [{
		type: "text",
		text: e.content ?? ""
	}];
}
function Z(e) {
	return e.map((e, t) => ({
		id: e.id ?? `history-${t}`,
		role: e.role,
		parts: Oe(e),
		metadata: {
			langfuseTraceId: e.langfuseTraceId,
			entityRefs: e.entityRefs,
			aiProvenance: e.aiProvenance,
			userFeedback: e.userFeedback,
			createdAt: e.createdAt
		}
	}));
}
function ke(e) {
	let t = /* @__PURE__ */ new Map();
	for (let n of e) {
		if (!n.id || n.role !== "user") continue;
		let e = (n.parts ?? []).flatMap((e) => e.type === "file" ? [{
			key: e.storageKey,
			filename: e.filename,
			mediaType: e.mediaType
		}] : []);
		t.set(n.id, e);
	}
	return t;
}
function Q(e) {
	switch (e) {
		case "output-available": return "output-available";
		case "output-error":
		case "output-denied": return "output-error";
		case "input-streaming": return "input-streaming";
		default: return "input-available";
	}
}
var Ae = /* @__PURE__ */ new Set(["refused", "conflict"]);
function je(e) {
	if (typeof e != "object" || !e) return !1;
	let { status: t } = e;
	return typeof t == "string" && Ae.has(t);
}
var Me = new Set(p);
function Ne(e) {
	return e.type === "text" ? [{
		type: "text",
		text: e.text
	}] : e.type === "file" ? [{
		type: "file",
		filename: e.filename,
		mediaType: re(e.mediaType) ? e.mediaType : void 0
	}] : L(e) ? Me.has(e.type) ? [{
		type: e.type,
		toolCallId: e.toolCallId,
		state: Q(e.state),
		...e.state === "output-available" ? { output: e.output } : {}
	}] : [{
		type: "tool",
		toolName: I(e),
		toolCallId: e.toolCallId,
		state: Q(e.state),
		...e.input === void 0 ? {} : { input: e.input },
		...je(e.output) ? { rejected: !0 } : {}
	}] : [];
}
var Pe = /* @__PURE__ */ new WeakMap();
function Fe(e) {
	let t = [];
	for (let n of e) {
		if (n.role !== "user" && n.role !== "assistant") continue;
		let e = Pe.get(n);
		if (e) {
			t.push(e);
			continue;
		}
		let r = Ie(n.metadata), i = {
			id: n.id,
			role: n.role,
			parts: n.parts.flatMap(Ne),
			...r ? { metadata: r } : {}
		};
		Pe.set(n, i), t.push(i);
	}
	return t;
}
function Ie(e) {
	if (typeof e != "object" || !e) return;
	let { entityRefs: t, langfuseTraceId: n, aiProvenance: r, userFeedback: i, createdAt: a } = e, o = {
		...t?.length ? { entityRefs: t } : {},
		...n ? { langfuseTraceId: n } : {},
		...r ? { aiProvenance: r } : {},
		...i ? { userFeedback: i } : {},
		...a ? { createdAt: a } : {}
	};
	return Object.keys(o).length > 0 ? o : void 0;
}
//#endregion
//#region src/react/hooks/use-app-chat.ts
var Le = 50, $ = {
	session: (e, t, n) => [
		"app-chat",
		e,
		t,
		"session",
		n
	],
	history: (e, t, n) => [
		"app-chat",
		e,
		t,
		"history",
		n
	]
}, Re = 409;
function ze(e) {
	if (!e) return !1;
	try {
		let t = JSON.parse(e.message);
		return typeof t == "object" && !!t && t.statusCode === Re;
	} catch {
		return !1;
	}
}
function Be({ kind: e, subjectKey: n, sessionId: s, enabled: c = !0, onSessionCreated: l, onError: d }) {
	let { apiBaseUrl: f, accessToken: p, app: m } = z(), h = B(), te = ee(), g = m.id, _ = n === void 0, v = u({
		queryKey: $.session(g, e, n ?? ""),
		queryFn: async () => {
			let t = { subjectKey: n }, { data: r } = await h.post(X(oe.path, {
				appId: g,
				kind: e
			}), t);
			return r;
		},
		enabled: c && n !== void 0 && n.length > 0,
		staleTime: Infinity,
		retry: !1
	}), [re, y] = o(void 0), b = s ?? v.data?.sessionId ?? re, x = a(s !== void 0);
	r(function() {
		x.current && s === void 0 && y(void 0), x.current = s !== void 0;
	}, [s]);
	let S = a(b);
	S.current = b;
	let C = u({
		queryKey: $.history(g, e, b),
		queryFn: async ({ signal: t }) => {
			let { data: n } = await h.get(X(ae.path, {
				appId: g,
				kind: e,
				sessionId: b
			}), { signal: t });
			return n;
		},
		enabled: !!b,
		refetchOnWindowFocus: !1
	}), w = C.data?.messages, se = i(() => w ? Z(w) : [], [w]), T = a([]), E = a(/* @__PURE__ */ new Map());
	E.current = i(() => w ? ke(w) : /* @__PURE__ */ new Map(), [w]);
	let D = i(() => new le({
		api: `${f}${X(ne.path, {
			appId: g,
			kind: e
		})}`,
		headers: () => ({ Authorization: `Bearer ${p}` }),
		prepareSendMessagesRequest: ({ messages: e }) => ({ body: Ve({
			messages: e,
			sessionId: S.current,
			composing: T.current,
			stored: E.current
		}) })
	}), [
		f,
		p,
		g,
		e
	]), O = ce({
		id: s ?? v.data?.sessionId ?? `app-chat:${g}:${e}:${n ?? "new"}`,
		messages: se,
		transport: D,
		throttle: 50,
		onFinish: ({ message: t, isError: n }) => {
			let r = t.metadata, i = n ? void 0 : r?.sessionId;
			i && !S.current && (S.current = i, y(i), l?.(i)), te.invalidateQueries({ queryKey: $.history(g, e, S.current) });
		},
		...d ? { onError: d } : {}
	}), { sendMessage: k, setMessages: A } = O, j = O.status === "streaming" || O.status === "submitted", M = a(!1);
	j || (M.current = !1);
	let N = a(void 0);
	r(function() {
		j || C.isFetching || !C.data || N.current === C.data || (N.current = C.data, A(Z(C.data.messages)));
	}, [
		b,
		C.data,
		C.isFetching,
		j,
		A
	]);
	let P = t(({ text: e, attachments: t = [] }) => !_ && !S.current || !e.trim() || M.current ? !1 : (M.current = !0, T.current = t, k({
		text: e,
		files: t.map((e) => ({
			type: "file",
			mediaType: e.mediaType,
			filename: e.filename,
			url: ""
		})),
		metadata: { createdAt: (/* @__PURE__ */ new Date()).toISOString() }
	}), !0), [k]), F = i(() => [...O.messages].reverse().find((e) => e.role === "user")?.id, [O.messages]), I = F !== void 0 && !ie(F), L = i(() => Fe(O.messages), [O.messages]), R = t((e) => (_ || !!b) && !j && !M.current && e.trim().length > 0, [
		_,
		b,
		j
	]);
	return {
		messages: L,
		sessionId: b,
		subjectRef: v.data?.subjectRef ?? C.data?.subjectRef,
		overlayPromptKey: v.data?.overlayPromptKey,
		isStarting: v.isPending && v.fetchStatus !== "idle",
		isHistoryLoading: C.isPending && C.fetchStatus !== "idle",
		isLoading: j,
		canSend: R,
		status: O.status,
		error: v.error ?? C.error ?? O.error ?? void 0,
		turnError: O.error ?? void 0,
		sendMessage: P,
		retry: I ? () => void O.regenerate() : void 0
	};
}
function Ve({ messages: e, sessionId: t, composing: n, stored: r }) {
	let i = e[e.length - 1], a = i?.parts?.find((e) => e.type === "text"), o = a && "text" in a ? a.text : "", s = i ? r.get(i.id) ?? n : n;
	return {
		sessionId: t,
		message: {
			id: i?.id,
			content: o
		},
		...s.length > 0 ? { attachments: s } : {}
	};
}
//#endregion
//#region src/react/hooks/use-app-chat-attachments.ts
var He = Math.floor(m / 1048576), Ue = {
	reason: "upload-failed",
	values: {}
}, We = {
	reason: "unsupported-type",
	values: {}
};
function Ge(e, t) {
	return t >= h ? {
		reason: "too-many",
		values: { max: h }
	} : e.size > m ? {
		reason: "too-large",
		values: { maxMegabytes: He }
	} : null;
}
function Ke({ kind: e, sessionId: t }) {
	let n = B(), { app: r } = z(), i = r.id;
	return {
		...F({
			reject: Ge,
			upload: async (t, { signal: r, onProgress: a, mediaType: o }) => {
				let s = {
					filename: t.name,
					mediaType: o,
					size: t.size
				}, { data: c } = await n.post(X(C.path, {
					appId: i,
					kind: e
				}), s);
				return await P(c.url, c.fields, t, {
					signal: r,
					onProgress: a
				}), { key: c.key };
			},
			uploadFailedRejection: Ue,
			unsupportedTypeRejection: We,
			...t ? { sessionKey: t } : {}
		}),
		accept: T
	};
}
//#endregion
//#region src/react/lib/xlsx-ai-provenance.ts
function qe(e) {
	let t = { aiGenerated: "true" };
	return e?.foundationModel && (t.foundationModel = e.foundationModel), e?.promptName && (t.promptName = e.promptName), e?.promptVersion !== void 0 && (t.promptVersion = String(e.promptVersion)), t;
}
function Je(e, t) {
	let n = e?.foundationModel, r = e?.promptVersion;
	return n && r !== void 0 ? t("excelAiNoteModelPrompt", {
		model: n,
		version: r
	}) : n ? t("excelAiNoteModel", { model: n }) : r === void 0 ? t("excelAiNote") : t("excelAiNotePrompt", { version: r });
}
function Ye(e, { title: t, provenance: n }) {
	e.Props = {
		Title: t,
		Author: "BLIKS IO",
		Keywords: "AI-generated"
	}, e.Custprops = qe(n);
}
//#endregion
//#region src/react/components/app-page.tsx
function Xe({ title: e, headerRight: t, tabs: n, activeTab: r, onTab: i, tabsEnd: a, children: o }) {
	return n ? /* @__PURE__ */ c(E, { children: [
		/* @__PURE__ */ s("h1", {
			className: "sr-only",
			children: e
		}),
		/* @__PURE__ */ s(O, { children: /* @__PURE__ */ c("div", {
			className: "flex items-center justify-between gap-4 border-b bg-sidebar pr-4",
			children: [/* @__PURE__ */ s(k, {
				value: r,
				onValueChange: i,
				children: /* @__PURE__ */ s(A, { children: n.map((e) => /* @__PURE__ */ s(j, {
					value: e.value,
					children: e.label
				}, e.value)) })
			}), a]
		}) }),
		/* @__PURE__ */ s(D, { children: o })
	] }) : /* @__PURE__ */ c("div", {
		className: "flex flex-col h-full",
		children: [/* @__PURE__ */ c("div", {
			className: "flex items-center justify-between gap-4 px-6 py-4 border-b border-border",
			children: [/* @__PURE__ */ s("h1", {
				className: "text-2xl font-semibold",
				children: e
			}), t]
		}), /* @__PURE__ */ s("div", {
			className: "flex-1 overflow-auto p-6",
			children: o
		})]
	});
}
//#endregion
//#region src/react/components/app-skeleton.tsx
function Ze() {
	return /* @__PURE__ */ c("div", {
		className: "flex flex-col h-full animate-pulse",
		children: [/* @__PURE__ */ s("div", {
			className: "px-6 py-4 border-b border-border",
			children: /* @__PURE__ */ s("div", { className: "h-8 w-48 bg-muted rounded" })
		}), /* @__PURE__ */ c("div", {
			className: "flex-1 p-6 space-y-4",
			children: [
				/* @__PURE__ */ s("div", { className: "h-4 w-full bg-muted rounded" }),
				/* @__PURE__ */ s("div", { className: "h-4 w-3/4 bg-muted rounded" }),
				/* @__PURE__ */ s("div", { className: "h-32 w-full bg-muted rounded" })
			]
		})]
	});
}
//#endregion
export { Le as APP_CHAT_STREAM_THROTTLE_MS, ue as AppContextProvider, Xe as AppPage, Ze as AppSkeleton, Z as appChatHistoryToUIMessages, $ as appChatQueryKeys, q as appCollectionQueryKey, J as appCollectionQueryOptions, Y as appDocumentQueryOptions, K as appFunctionQueryKey, Je as buildAiNote, X as buildAppChatPath, ke as collectAppChatHistoryAttachments, ze as isAppChatSessionBusyError, Ye as markWorkbookAiGenerated, me as processQueryOptions, Fe as toAiChatPanelMessages, de as useAiFeature, B as useAppApiClient, Be as useAppChat, Ke as useAppChatAttachments, Te as useAppCollection, z as useAppContext, Ee as useAppDocument, be as useAppFunction, xe as useAppFunctionQuery, ye as useAssigneeLookup, Se as useAsyncAppFunction, W as useExternalContactsForLookup, H as useOrgFunctionsForLookup, V as useOrgMembersForLookup, ge as useProcess, _e as useProcessChainTree, ve as useProcessLayout, pe as useProcesses, Ce as useRegionTiles, U as useSystemsForLookup, we as useTranscribeAudio };
